import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { buildEmbedFrameUrl, type EmbedLang } from "./snippet";

/**
 * Live preview of the widget: the real booking widget embedded directly in a cross-origin iframe
 * (`book.<domain>/v/<slug>?channel=embed`), sized to a fixed-height box.
 *
 * Why NOT the loader/srcdoc here: the loader mounts an iframe then waits for the widget to post
 * `ready`/`resize` back so it can size (inline) or dismiss its fallback. Inside the app's isolated
 * preview context the framed widget has no usable referrer to identify a safe parent origin, so it
 * stays silent and the loader always times out to its "Réserver" fallback anchor — the manager sees a
 * link, never the form. Embedding the widget URL directly needs no handshake: the widget renders its
 * booking flow immediately, and the fixed-height iframe scrolls. The copy-snippet (with the manager's
 * chosen floating/inline mode) is generated separately in EmbedSettings — this is just the visual proof.
 *
 * `channel=embed` keeps the widget in framed (full-bleed) mode, matching a real embed. In local dev
 * without a reachable `book.<domain>` the pane stays empty — hence the caption. Keyed by the URL so a
 * slug/language change re-mounts a fresh frame.
 */
export function EmbedPreview({
  origin,
  slug,
  lang,
}: {
  origin: string;
  slug: string;
  lang: EmbedLang;
}) {
  const { t } = useTranslation();
  const src = buildEmbedFrameUrl({ origin, slug, lang });
  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
      <h3 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
        {t("embed.preview.title")}
      </h3>
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("embed.preview.hint")}</p>
      <iframe
        key={src}
        title={t("embed.preview.title")}
        src={src}
        // Our OWN first-party booking app on book.<domain>. `allow-same-origin` keeps it on its real
        // cross-origin (so the widget's same-origin i18n storage works); `allow-scripts`/`allow-forms`
        // run the booking flow. scripts+same-origin only defeats a sandbox for SAME-origin content — this
        // frame is cross-origin, so the origin boundary is the real isolation, and the frame is never host input.
        // eslint-disable-next-line react/iframe-missing-sandbox -- cross-origin first-party widget frame
        sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
        className={css({
          width: "100%",
          height: "520px",
          maxW: "480px",
          borderWidth: "1px",
          borderColor: "border.default",
          rounded: "lg",
          bg: "bg.subtle",
        })}
      />
    </section>
  );
}
